import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface DocumentState {
  // Document viewing
  selectedDocType: string;
  selectedDocNumber: string;
  selectedDocName: string;
  selectedResourceType: string;
  selectedResourceNumber: string;
  prosDoc: Record<string, unknown>;

  // Document window state
  showDocWindow: boolean;
  docPanelWidth: number;
  showFiguresInDocWindow: unknown | null;

  // Document scrolling
  paragraphNumberToScrollTo: number | null;
  claimNumberToScrollTo: number | null;

  // Actions
  setSelectedDocType: (type: string) => void;
  setSelectedDocNumber: (number: string) => void;
  setSelectedDocName: (name: string) => void;
  setSelectedResourceType: (type: string) => void;
  setSelectedResourceNumber: (number: string) => void;
  setProsDoc: (doc: Record<string, unknown>) => void;

  setShowDocWindow: (show: boolean) => void;
  setDocPanelWidth: (width: number) => void;
  setShowFiguresInDocWindow: (figures: unknown | null) => void;

  setParagraphNumberToScrollTo: (num: number | null) => void;
  setClaimNumberToScrollTo: (num: number | null) => void;

  // Composite actions
  openDocument: (type: string, number: string, name: string) => void;
  closeDocWindow: () => void;
}

const initialDocPanelWidth = () => {
  if (typeof window === 'undefined') return 650;
  const halfScreen = Math.floor(window.innerWidth / 2);
  const maxWidth = window.innerWidth - 530;
  return Math.max(650, Math.min(halfScreen, maxWidth));
};

export const useDocumentStore = create<DocumentState>()(
  devtools((set) => ({
    // Initial state
    selectedDocType: '',
    selectedDocNumber: '',
    selectedDocName: '',
    selectedResourceType: '',
    selectedResourceNumber: '',
    prosDoc: {},

    showDocWindow: false,
    docPanelWidth: initialDocPanelWidth(),
    showFiguresInDocWindow: null,

    paragraphNumberToScrollTo: null,
    claimNumberToScrollTo: null,

    // Simple setters
    setSelectedDocType: (type) => set({ selectedDocType: type }),
    setSelectedDocNumber: (number) => set({ selectedDocNumber: number }),
    setSelectedDocName: (name) => set({ selectedDocName: name }),
    setSelectedResourceType: (type) => set({ selectedResourceType: type }),
    setSelectedResourceNumber: (number) => set({ selectedResourceNumber: number }),
    setProsDoc: (doc) => set({ prosDoc: doc }),

    setShowDocWindow: (show) => set({ showDocWindow: show }),
    setDocPanelWidth: (width) => set({ docPanelWidth: width }),
    setShowFiguresInDocWindow: (figures) => set({ showFiguresInDocWindow: figures }),

    setParagraphNumberToScrollTo: (num) => set({ paragraphNumberToScrollTo: num }),
    setClaimNumberToScrollTo: (num) => set({ claimNumberToScrollTo: num }),

    // Composite actions
    openDocument: (type, number, name) => set({
      selectedDocType: type,
      selectedDocNumber: number,
      selectedDocName: name,
      showDocWindow: true,
    }),

    closeDocWindow: () => set({
      showDocWindow: false,
      paragraphNumberToScrollTo: null,
      claimNumberToScrollTo: null,
    }),
  }))
);
